import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

interface NavbarProps {
  currentLang: string;
  onLangChange: (lang: string) => void;
}

export default function Navbar({ currentLang, onLangChange }: NavbarProps) {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(true);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  // Text colors adapt to the hero (dark image at top) vs. scrolled white bar
  const textColor = scrolled ? 'text-foreground-700' : 'text-white';
  const hoverColor = scrolled ? 'hover:text-foreground-950' : 'hover:text-white';
  const logoColor = scrolled ? 'text-foreground-950' : 'text-white';

  const langs = [
    { code: 'en', label: t('lang.en') },
    { code: 'fr', label: t('lang.fr') },
    { code: 'da', label: t('lang.da') },
    { code: 'de', label: t('lang.de') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-lg md:text-xl font-bold tracking-tight ${logoColor}`}>
              {t('site.name')}
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6 lg:gap-7">
            <a href="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}>
              {t('nav.home')}
            </a>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}
              >
                {t('categories.all')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-background-200 py-2 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                      onClick={() => setCatOpen(false)}
                    >
                      <span className="w-9 h-9 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      <span className="flex-1 truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a href="/articles/allchinabuy-spreadsheet-complete-guide/" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}>
              {t('nav.shoppingGuide')}
            </a>

            <a href="/articles/allchinabuy-spreadsheet-complete-guide/" className={`text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}>
              {t('nav.articles')}
            </a>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}
              >
                <i className="ri-global-line" />
                {t('nav.language')}
                <i className={`ri-arrow-down-s-line transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-background-200 py-2">
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        onLangChange(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        currentLang === l.code
                          ? 'text-primary-600 bg-primary-50'
                          : 'text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Main Site CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav.mainSite')}
              <i className="ri-external-link-line" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 ${textColor}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200 shadow-lg">
          <div className="px-4 py-4 space-y-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Primary actions */}
            <div className="grid grid-cols-2 gap-2">
              <a
                href="/"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-white text-sm font-medium"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-home-4-line" />
                {t('nav.home')}
              </a>
              <a
                href="/articles/allchinabuy-spreadsheet-complete-guide/"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-secondary-500 text-white text-sm font-medium"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-book-open-line" />
                {t('nav.shoppingGuide')}
              </a>
            </div>

            {/* Categories accordion */}
            <div className="border border-background-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-background-50 text-sm font-semibold text-foreground-900"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-grid-line" />
                  {t('categories.all')}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 p-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-foreground-700 bg-background-50 hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-background-200">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </span>
                      <span className="truncate text-xs">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Articles */}
            <a
              href="/articles/allchinabuy-spreadsheet-complete-guide/"
              className="flex items-center gap-2 text-sm font-medium text-foreground-700 py-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-article-line" />
              {t('nav.articles')}
            </a>

            <hr className="border-background-200" />

            {/* Language */}
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">{t('nav.language')}</p>
            <div className="flex gap-2 flex-wrap">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => {
                    onLangChange(l.code);
                    setMobileOpen(false);
                  }}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    currentLang === l.code
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Main site */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-foreground-950 text-white text-sm font-medium"
            >
              {t('nav.mainSite')}
              <i className="ri-external-link-line" />
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}